import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterIcon, Download as DownloadIcon, Sort as SortIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon } from '@mui/icons-material';
import { EntityMultiSelect } from '@/components/common/EntityMultiSelect';

interface LeadsFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  streams: string[];
  onStreamsChange: (value: string[]) => void;
  categories: string[];
  onCategoriesChange: (value: string[]) => void;
  onExport: () => void;
  availableCategories: string[];
  streamsOptions: Array<{ uuid: string; name?: string | null }>;
  operatorFilter: string | null;
  onOperatorFilterChange: (value: string | null) => void;
  teamFilter: string | null;
  onTeamFilterChange: (value: string | null) => void;
  activityFilter: string | null;
  onActivityFilterChange: (value: string | null) => void;
  happenedAfter: string;
  onHappenedAfterChange: (value: string) => void;
  happenedBefore: string;
  onHappenedBeforeChange: (value: string) => void;
  availableActivities: string[];
  sortField: string;
  onSortFieldChange: (value: string) => void;
  sortDirection: 'asc' | 'desc';
  onSortDirectionChange: (value: 'asc' | 'desc') => void;
  sortOptions: Array<{ value: string; label: string }>;
  allOperators: Array<{ name: string; uuid: string }>;
  allTeams: Array<{ key: string; name: string }>;
  isLoadingOperators: boolean;
  isLoadingTeams: boolean;
  hasNextOperatorsPage: boolean;
  hasNextTeamsPage: boolean;
  onFetchNextOperatorsPage: () => void;
  onFetchNextTeamsPage: () => void;
}

export const LeadsFilterBar: React.FC<LeadsFilterBarProps> = ({
  search,
  onSearchChange,
  streams,
  onStreamsChange,
  categories,
  onCategoriesChange,
  onExport,
  availableCategories,
  streamsOptions,
  operatorFilter,
  onOperatorFilterChange,
  teamFilter,
  onTeamFilterChange,
  activityFilter,
  onActivityFilterChange,
  happenedAfter,
  onHappenedAfterChange,
  happenedBefore,
  onHappenedBeforeChange,
  availableActivities,
  sortField,
  onSortFieldChange,
  sortDirection,
  onSortDirectionChange,
  sortOptions,
  allOperators,
  allTeams,
  isLoadingOperators,
  isLoadingTeams,
  hasNextOperatorsPage,
  hasNextTeamsPage,
  onFetchNextOperatorsPage,
  onFetchNextTeamsPage,
}) => {
  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
    '& .MuiOutlinedInput-notchedOutline': { borderRadius: 2 },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        mb: 2,
      }}
    >
      {/* Row 1: search, experiments, step categories, operator, team */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Search leads..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: 260 },
            ...inputSx,
          }}
        />

        {/* Experiments duplicate-safe multi-select */}
        <EntityMultiSelect
          options={streamsOptions}
          valueUuids={streams}
          onChange={onStreamsChange}
          placeholder="Select experiments"
          label="Experiments"
          size="small"
          renderTags={(value) =>
            value.length > 0 ? (
              <Chip
                key="count"
                size="small"
                label={`${value.length} selected`}
                onDelete={() => onStreamsChange([])}
              />
            ) : null
          }
          autocompleteSx={{
            minWidth: { xs: '100%', sm: 220 },
            '& .MuiAutocomplete-inputRoot': { borderRadius: 2 },
          }}
          textFieldSx={inputSx}
        />

        {/* Step categories multi-select (checkbox list) */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 240 }, width: { xs: '100%', sm: 'auto' } }}
        >
          <InputLabel id="categories-label">Step categories</InputLabel>
          <Select
            labelId="categories-label"
            multiple
            value={categories}
            onChange={(e) => onCategoriesChange(e.target.value as string[])}
            input={<OutlinedInput label="Step categories" />}
            startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1 }} />}
            renderValue={(selected) => (selected.length > 0 ? `${selected.length} selected` : '')}
            sx={{
              '& .MuiSelect-select': {
                padding: '8px 12px',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
              },
              ...inputSx,
            }}
          >
            {availableCategories.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={categories.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Operator Filter - Single select with autocomplete */}
        <Autocomplete
            size="small"
            options={allOperators}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            value={allOperators.find((op) => op.uuid === operatorFilter) || null}
            onChange={(_, newValue) => {
              onOperatorFilterChange(newValue?.uuid || null);
            }}
            loading={isLoadingOperators}
            filterOptions={(options, state) => {
              // Filter based on current input value
              if (!state.inputValue) {
                return options;
              }
              const searchLower = state.inputValue.toLowerCase();
              return options.filter((option) =>
                option.name.toLowerCase().includes(searchLower)
              );
            }}
            ListboxProps={{
              onScroll: (event: React.SyntheticEvent) => {
                const listboxNode = event.currentTarget;
                if (
                  listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                  hasNextOperatorsPage &&
                  !isLoadingOperators
                ) {
                  onFetchNextOperatorsPage();
                }
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search operators..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <FilterIcon fontSize="small" sx={{ mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{
                  minWidth: { xs: '100%', sm: 200 },
                  ...inputSx,
                }}
              />
            )}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: '100%', sm: 200 },
            }}
          />

        {/* Team Filter - Single select with autocomplete */}
        <Autocomplete
          size="small"
          options={allTeams}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          value={allTeams.find((team) => team.key === teamFilter) || null}
          onChange={(_, newValue) => {
            onTeamFilterChange(newValue?.key || null);
          }}
          loading={isLoadingTeams}
          filterOptions={(options, state) => {
            // Filter based on current input value
            if (!state.inputValue) {
              return options;
            }
            const searchLower = state.inputValue.toLowerCase();
            return options.filter((option) =>
              option.name.toLowerCase().includes(searchLower)
            );
          }}
          ListboxProps={{
            onScroll: (event: React.SyntheticEvent) => {
              const listboxNode = event.currentTarget;
              if (
                listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                hasNextTeamsPage &&
                !isLoadingTeams
              ) {
                onFetchNextTeamsPage();
              }
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search teams..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <FilterIcon fontSize="small" sx={{ mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                ...inputSx,
              }}
            />
          )}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { xs: '100%', sm: 200 },
          }}
        />
      </Box>

      {/* Row 2: activity filters + export at right */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, ...inputSx }}>
            <InputLabel>Action/Event</InputLabel>
            <Select
              value={activityFilter || ''}
              label="Action/Event"
              onChange={(e) => onActivityFilterChange(e.target.value ? String(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>All actions/events</em>
              </MenuItem>
              {availableActivities.map((activity) => (
                <MenuItem key={activity} value={activity}>
                  {activity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Happened After"
            type="datetime-local"
            value={happenedAfter}
            onChange={(e) => onHappenedAfterChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', sm: 220 }, ...inputSx }}
          />

          <TextField
            size="small"
            label="Happened Before"
            type="datetime-local"
            value={happenedBefore}
            onChange={(e) => onHappenedBeforeChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', sm: 220 }, ...inputSx }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 }, ...inputSx }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortField}
              label="Sort By"
              onChange={(e) => onSortFieldChange(e.target.value)}
              startAdornment={<SortIcon fontSize="small" sx={{ mr: 1 }} />}
            >
              {sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={`Sort ${sortDirection === 'desc' ? 'Ascending' : 'Descending'}`}>
            <IconButton
              size="small"
              onClick={() => onSortDirectionChange(sortDirection === 'desc' ? 'asc' : 'desc')}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1 }}
            >
              {sortDirection === 'desc' ? (
                <ArrowDownIcon fontSize="small" />
              ) : (
                <ArrowUpIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={onExport}
          sx={{ borderRadius: 2, flexShrink: 0 }}
        >
          Export
        </Button>
      </Box>

      {/* Row 3: active tags */}
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ alignItems: 'center' }}>
        {streams.length > 0 && (
          <Chip
            label={`Experiments: ${streams.length}`}
            size="small"
            onDelete={() => onStreamsChange([])}
            color="primary"
            variant="outlined"
          />
        )}
        {categories.map((category) => (
          <Chip
            key={`category-${category}`}
            label={`Step: ${category}`}
            size="small"
            onDelete={() => onCategoriesChange(categories.filter((c) => c !== category))}
            color="primary"
            variant="outlined"
          />
        ))}
        {operatorFilter && (
          <Chip
            label={`Operator: ${allOperators.find((op) => op.uuid === operatorFilter)?.name || operatorFilter}`}
            size="small"
            onDelete={() => onOperatorFilterChange(null)}
            color="info"
            variant="outlined"
          />
        )}
        {teamFilter && (
          <Chip
            label={`Team: ${allTeams.find((team) => team.key === teamFilter)?.name || teamFilter}`}
            size="small"
            onDelete={() => onTeamFilterChange(null)}
            color="secondary"
            variant="outlined"
          />
        )}
        {search && (
          <Chip
            label={`Search: "${search}"`}
            size="small"
            onDelete={() => onSearchChange('')}
            color="secondary"
            variant="outlined"
          />
        )}
        {activityFilter && (
          <Chip
            label={`Activity: ${activityFilter}`}
            size="small"
            onDelete={() => onActivityFilterChange(null)}
            color="warning"
            variant="outlined"
          />
        )}
        {happenedAfter && (
          <Chip
            label={`From: ${happenedAfter.replace('T', ' ')}`}
            size="small"
            onDelete={() => onHappenedAfterChange('')}
            color="warning"
            variant="outlined"
          />
        )}
        {happenedBefore && (
          <Chip
            label={`To: ${happenedBefore.replace('T', ' ')}`}
            size="small"
            onDelete={() => onHappenedBeforeChange('')}
            color="warning"
            variant="outlined"
          />
        )}
        {(sortField !== 'created_at' || sortDirection !== 'desc') && (
          <Chip
            label={`Sort: ${sortOptions.find((o) => o.value === sortField)?.label || sortField} ${sortDirection}`}
            size="small"
            onDelete={() => {
              onSortFieldChange('created_at');
              onSortDirectionChange('desc');
            }}
            color="default"
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

